/**
 * Authenticated fetch for Bastion API routes where hooks should not manage
 * tokens manually. Some older /episodes/* operations still use BastionApiClient,
 * but newer domain hooks use this utility so auth remains centralized.
 */

import { getBastionApiBaseUrl, BastionApiError } from "@bastion/api";
import { getSupabaseAccessToken } from "./supabase";

export interface AuthenticatedFetchOptions extends RequestInit {
  /**
   * Base URL to hit instead of the bastion API. Used by the telemetry query
   * hooks, which target a separate service on its own subdomain but share the
   * same Supabase bearer token.
   */
  baseUrl?: string;
  /** Treat HTTP 202 as failure. Query routes use this while the index builds. */
  rejectPendingResponse?: boolean;
}

/**
 * Fetch from Bastion API with auth. Automatically attaches Bearer token.
 */
export async function authenticatedFetch<T>(
  path: string,
  options?: AuthenticatedFetchOptions,
): Promise<T> {
  const token = await getSupabaseAccessToken();
  const {
    baseUrl: baseUrlOverride,
    rejectPendingResponse = false,
    ...requestInit
  } = options ?? {};
  const baseUrl = baseUrlOverride ?? getBastionApiBaseUrl();
  const url = `${baseUrl}${path}`;

  const isFormData = requestInit.body instanceof FormData;
  const resp = await fetch(url, {
    ...requestInit,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token}`,
      ...(requestInit.headers as Record<string, string>),
    },
  });

  if (!resp.ok || (rejectPendingResponse && resp.status === 202)) {
    const text = await resp.text().catch(() => "");
    throw new BastionApiError(
      `Bastion API error (${path}): ${resp.status}`,
      resp.status,
      text,
    );
  }

  if (resp.status === 204) {
    return {} as T;
  }
  return resp.json() as Promise<T>;
}
